import React, { useEffect, useState } from 'react';
import { SaveIcon } from 'lucide-react';
export function CountryDetail({
  country,
  onSave,
  onCancel,
  isCreating,
  locale
}) {
  const [formData, setFormData] = useState({
    alpha2Code: '',
    alpha3Code: '',
    numericCode: '',
    name: '',
    officialCurrency: '',
    phoneCode: '',
    region: '',
    subRegion: '',
    customFields: {
      vatApplicable: 'false',
      defaultTaxRate: '0',
      euMember: 'false'
    }
  });
  // Available regions and subregions for dropdown selection
  const regions = ['Africa', 'Americas', 'Asia', 'Europe', 'Oceania'];
  const subRegionsByRegion = {
    Africa: ['Northern Africa', 'Eastern Africa', 'Middle Africa', 'Southern Africa', 'Western Africa'],
    Americas: ['Northern America', 'Caribbean', 'Central America', 'South America'],
    Asia: ['Central Asia', 'Eastern Asia', 'Southern Asia', 'South-Eastern Asia', 'Western Asia'],
    Europe: ['Eastern Europe', 'Northern Europe', 'Southern Europe', 'Western Europe'],
    Oceania: ['Australia and New Zealand', 'Melanesia', 'Micronesia', 'Polynesia']
  };
  // Common currencies for quick selection
  const commonCurrencies = [{
    code: 'USD',
    name: 'US Dollar'
  }, {
    code: 'EUR',
    name: 'Euro'
  }, {
    code: 'GBP',
    name: 'British Pound'
  }, {
    code: 'JPY',
    name: 'Japanese Yen'
  }, {
    code: 'AUD',
    name: 'Australian Dollar'
  }, {
    code: 'CAD',
    name: 'Canadian Dollar'
  }, {
    code: 'CHF',
    name: 'Swiss Franc'
  }, {
    code: 'CNY',
    name: 'Chinese Yuan'
  }, {
    code: 'INR',
    name: 'Indian Rupee'
  }, {
    code: 'SGD',
    name: 'Singapore Dollar'
  }];
  useEffect(() => {
    if (country && !isCreating) {
      setFormData({
        ...country,
        customFields: {
          ...country.customFields
        }
      });
    } else {
      // Default values for new country
      setFormData({
        alpha2Code: '',
        alpha3Code: '',
        numericCode: '',
        name: '',
        officialCurrency: '',
        phoneCode: '',
        region: '',
        subRegion: '',
        customFields: {
          vatApplicable: 'false',
          defaultTaxRate: '0',
          euMember: 'false'
        }
      });
    }
  }, [country, isCreating]);
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleCustomFieldChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      customFields: {
        ...formData.customFields,
        [name]: value
      }
    });
  };
  const handleRegionChange = e => {
    const newRegion = e.target.value;
    setFormData({
      ...formData,
      region: newRegion,
      // Reset subregion when region changes
      subRegion: ''
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    onSave(formData);
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-14701">
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-14702">
        <div data-id="element-14703">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-14704">
            {isCreating ? 'Add New Country' : 'Edit Country'}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-14705">
            {isCreating ? 'Enter details for the new country' : 'Update country details'}
          </p>
        </div>
        <div className="flex space-x-3" data-id="element-14706">
          <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14707">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14708">
            <SaveIcon className="h-4 w-4 mr-2" data-id="element-14709" />
            Save Country
          </button>
        </div>
      </div>
      <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-14710">
        <form className="space-y-6" onSubmit={handleSubmit} data-id="element-14711">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-14712">
            {/* Basic Information */}
            <div className="sm:col-span-6" data-id="element-14713">
              <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-14714">
                Basic Information
              </h4>
            </div>
            <div className="sm:col-span-3" data-id="element-14715">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-14716">
                Country Name
              </label>
              <div className="mt-1" data-id="element-14717">
                <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-14718" />
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-14719">
              <label htmlFor="phoneCode" className="block text-sm font-medium text-gray-700" data-id="element-14720">
                Phone Code
              </label>
              <div className="mt-1" data-id="element-14721">
                <input type="text" name="phoneCode" id="phoneCode" value={formData.phoneCode} onChange={handleChange} placeholder="+XX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-14722" />
              </div>
            </div>
            {/* ISO Codes */}
            <div className="sm:col-span-6" data-id="element-14723">
              <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-14724">
                ISO Codes
              </h4>
            </div>
            <div className="sm:col-span-2" data-id="element-14725">
              <label htmlFor="alpha2Code" className="block text-sm font-medium text-gray-700" data-id="element-14726">
                Alpha-2 Code (ISO 3166-1)
              </label>
              <div className="mt-1" data-id="element-14727">
                <input type="text" name="alpha2Code" id="alpha2Code" value={formData.alpha2Code} onChange={handleChange} maxLength={2} placeholder="XX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md uppercase" required disabled={!isCreating} // Alpha-2 code cannot be changed after creation
              data-id="element-14728" />
              </div>
              {!isCreating && <p className="mt-1 text-xs text-gray-500" data-id="element-14729">
                  Alpha-2 code cannot be changed after creation
                </p>}
            </div>
            <div className="sm:col-span-2" data-id="element-14730">
              <label htmlFor="alpha3Code" className="block text-sm font-medium text-gray-700" data-id="element-14731">
                Alpha-3 Code (ISO 3166-1)
              </label>
              <div className="mt-1" data-id="element-14732">
                <input type="text" name="alpha3Code" id="alpha3Code" value={formData.alpha3Code} onChange={handleChange} maxLength={3} placeholder="XXX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md uppercase" required data-id="element-14733" />
              </div>
            </div>
            <div className="sm:col-span-2" data-id="element-14734">
              <label htmlFor="numericCode" className="block text-sm font-medium text-gray-700" data-id="element-14735">
                Numeric Code (ISO 3166-1)
              </label>
              <div className="mt-1" data-id="element-14736">
                <input type="text" name="numericCode" id="numericCode" value={formData.numericCode} onChange={handleChange} maxLength={3} placeholder="XXX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-14737" />
              </div>
            </div>
            {/* Geography */}
            <div className="sm:col-span-6" data-id="element-14738">
              <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-14739">
                Geography
              </h4>
            </div>
            <div className="sm:col-span-3" data-id="element-14740">
              <label htmlFor="region" className="block text-sm font-medium text-gray-700" data-id="element-14741">
                Region
              </label>
              <div className="mt-1" data-id="element-14742">
                <select id="region" name="region" value={formData.region} onChange={handleRegionChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-14743">
                  <option value="" data-id="element-14744">Select a region</option>
                  {regions.map(region => <option key={region} value={region} data-id="element-14745">
                      {region}
                    </option>)}
                </select>
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-14746">
              <label htmlFor="subRegion" className="block text-sm font-medium text-gray-700" data-id="element-14747">
                Sub-Region
              </label>
              <div className="mt-1" data-id="element-14748">
                <select id="subRegion" name="subRegion" value={formData.subRegion} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" disabled={!formData.region} required data-id="element-14749">
                  <option value="" data-id="element-14750">Select a sub-region</option>
                  {formData.region && subRegionsByRegion[formData.region]?.map(subRegion => <option key={subRegion} value={subRegion} data-id="element-14751">
                        {subRegion}
                      </option>)}
                </select>
              </div>
            </div>
            {/* Currency */}
            <div className="sm:col-span-6" data-id="element-14752">
              <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-14753">
                Currency
              </h4>
            </div>
            <div className="sm:col-span-3" data-id="element-14754">
              <label htmlFor="officialCurrency" className="block text-sm font-medium text-gray-700" data-id="element-14755">
                Official Currency
              </label>
              <div className="mt-1" data-id="element-14756">
                <select id="officialCurrency" name="officialCurrency" value={formData.officialCurrency} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" required data-id="element-14757">
                  <option value="" data-id="element-14758">Select a currency</option>
                  {commonCurrencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-14759">
                      {currency.code} - {currency.name}
                    </option>)}
                  <option value="OTHER" data-id="element-14760">Other (enter manually)</option>
                </select>
              </div>
            </div>
            {formData.officialCurrency === 'OTHER' && <div className="sm:col-span-3" data-id="element-14761">
                <label htmlFor="customCurrency" className="block text-sm font-medium text-gray-700" data-id="element-14762">
                  Custom Currency Code
                </label>
                <div className="mt-1" data-id="element-14763">
                  <input type="text" name="officialCurrency" id="customCurrency" value={formData.officialCurrency === 'OTHER' ? '' : formData.officialCurrency} onChange={handleChange} maxLength={3} placeholder="XXX" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md uppercase" required data-id="element-14764" />
                </div>
              </div>}
            {/* Custom Fields */}
            <div className="sm:col-span-6" data-id="element-14765">
              <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-14766">
                Tax & Regulatory
              </h4>
            </div>
            <div className="sm:col-span-2" data-id="element-14767">
              <label htmlFor="vatApplicable" className="block text-sm font-medium text-gray-700" data-id="element-14768">
                VAT/GST Applicable
              </label>
              <div className="mt-1" data-id="element-14769">
                <select id="vatApplicable" name="vatApplicable" value={formData.customFields.vatApplicable} onChange={handleCustomFieldChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-14770">
                  <option value="true" data-id="element-14771">Yes</option>
                  <option value="false" data-id="element-14772">No</option>
                </select>
              </div>
            </div>
            <div className="sm:col-span-2" data-id="element-14773">
              <label htmlFor="defaultTaxRate" className="block text-sm font-medium text-gray-700" data-id="element-14774">
                Default Tax Rate (%)
              </label>
              <div className="mt-1" data-id="element-14775">
                <input type="number" name="defaultTaxRate" id="defaultTaxRate" value={formData.customFields.defaultTaxRate} onChange={handleCustomFieldChange} min="0" max="100" step="0.01" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-14776" />
              </div>
            </div>
            <div className="sm:col-span-2" data-id="element-14777">
              <label htmlFor="euMember" className="block text-sm font-medium text-gray-700" data-id="element-14778">
                EU Member
              </label>
              <div className="mt-1" data-id="element-14779">
                <select id="euMember" name="euMember" value={formData.customFields.euMember} onChange={handleCustomFieldChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-14780">
                  <option value="true" data-id="element-14781">Yes</option>
                  <option value="false" data-id="element-14782">No</option>
                </select>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-3" data-id="element-14783">
            <button type="button" onClick={onCancel} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14784">
              Cancel
            </button>
            <button type="submit" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14785">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-14786" />
              {isCreating ? 'Create Country' : 'Update Country'}
            </button>
          </div>
        </form>
      </div>
    </div>;
}